import React, {useMemo, useState} from 'react'
import {compose, fromRenderProps} from 'recompose'
import {Button} from 'antd'
import {AiOutlineCheck, AiOutlineClose} from 'react-icons/ai'
import classNames from 'classnames'
import dayjs from 'dayjs'
import useTranslate from 'modules/local/useTranslate'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault} from 'views/Shared'
import {getId, getType} from 'apis/model/base'
import {useRouteSearchParam} from 'envs/_current/history'
import WorkspaceContext from '../../WorkspaceContext'
import {moderation_getOrganizationPendingItems_Api} from '../../../../apis/moderation'
import useApproval from '../functions/useApproval'
import {GoDotFill} from "react-icons/go";
import {BsPersonFill} from "react-icons/bs";

// ─── Constants ────────────────────────────────────────────────────────────────

const filter_actions = {
    sortBy:   'sort-by',
    keyword:  'keyword',
    type:     'type',
}

const TYPE_BADGE_COLORS = {
    default:      {bg: '#f0f0f0', text: '#888888'},
    post:         {bg: '#ffe4e6', text: '#be123c'},
    article:      {bg: '#dbeafe', text: '#1d4ed8'},
    ask:          {bg: '#dcfce7', text: '#15803d'},
    poll_question:{bg: '#fef9c3', text: '#a16207'},
}

const CONTENT_TYPE_OPTIONS = ['post', 'article', 'ask', 'poll_question']

// ─── Type badge ───────────────────────────────────────────────────────────────

const TypeBadge = ({label, type}) => {
    const colors = TYPE_BADGE_COLORS[type] ?? TYPE_BADGE_COLORS.default
    return (
        <span
            style={{background: colors.bg, color: colors.text, padding: '2px 10px'}}
            className="rounded-full font-medium text-2xs italic tracking-wider whitespace-no-wrap shrink-0">
            {label}
        </span>
    )
}

// ─── Filter component ─────────────────────────────────────────────────────────

const ModerationFilter = ({value: filter = {}, onChange: setValues = NullAsDefault}) => {
    const t = useTranslate()
    return (
        <div className="my-2 flex flex-wrap items-center gap-0.5">
            <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                {t('content type')}
            </span>
            {CONTENT_TYPE_OPTIONS.map(type => {
                const isActive = filter?.type === type
                const colors = TYPE_BADGE_COLORS[type] ?? TYPE_BADGE_COLORS.default
                return (
                    <span
                        key={type}
                        onClick={() => setValues({
                            ...filter,
                            type: isActive ? undefined : type,
                        })}
                        style={isActive ? {background: colors.bg, color: colors.text} : {}}
                        className={classNames(
                            'px-3 py-1 font-medium text-xs border rounded-lg select-none cursor-pointer transition-colors duration-300',
                            isActive
                                ? 'border-current'
                                : 'border-transparent background shadow hover:bg-secondary-50 text-color-300 hover:text-secondary'
                        )}>
                        {t(type) || type}
                    </span>
                )
            })}
        </div>
    )
}

// ─── Single row item ──────────────────────────────────────────────────────────

const ModerationItem = ({item, onApprove, onReject}) => {
    const t = useTranslate()
    const [busy, setBusy] = useState(null)

    const content     = item?.content
    const contentType = item?.content_type
    const title       = content?.overview?.title || content?.description || content?.title || t('untitled')
    const owner       = content?.owner
    const container   = content?.container
    const ownerName   = owner?.name ?? owner?.username ?? '—'
    const channelName = container?.title ?? container?.name ?? null
    const createdAt   = item?.created_at

    const handleApprove = () => {
        setBusy('approve')
        onApprove({}, {type: getType(content), id: getId(content)})
    }

    const handleReject = () => {
        setBusy('reject')
        onReject({}, {type: getType(content), id: getId(content)})
    }

    return (
        <div className="p-4 transition-all duration-300 relative list-item">
            {/* Content info */}
            <div className="flex flex-col flex-1">
                {/* Title + badge */}
                <div className="flex items-start gap-2">
                    <span className="font-bold text-lg text-color-000 leading-snug flex-1 min-w-0">
                        {title}
                    </span>
                    {!!contentType && (
                        <TypeBadge label={t(contentType) || contentType} type={contentType}/>
                    )}
                </div>
                <span className="flex items-center gap-1 text-blue-500 font-medium">
                    <BsPersonFill size={13} className="mr-1"/>
                    {ownerName}
                </span>
                <div className="mt-2 pt-2 border-t border-color-50 flex items-center gap-2">
                    {/* Meta: poster · channel · time */}
                    <div className="flex flex-col flex-1">
                        {!!channelName && (
                            <span className="space-x-1 text-color-200 italic">
                            <GoDotFill size={10} className="mr-1 text-color-500" />
                                {channelName}
                            </span>
                        )}
                        {!!createdAt && (
                            <span className="space-x-1 text-color-200 italic">
                            <GoDotFill size={10} className="mr-1 text-color-500" />
                                {dayjs(createdAt).fromNow()}
                            </span>
                        )}
                    </div>
                    {/* Approve / Reject */}
                    <div className="flex items-center gap-2 shrink-0">
                        <Button
                            size="small" shape="round" variant="filled"
                            loading={busy === 'approve'}
                            disabled={!!busy}
                            onClick={handleApprove}
                            className="border-green-500 text-green-600 bg-green-50"
                            icon={<AiOutlineCheck size={13}/>}
                        />
                        <Button
                            size="small" shape="round"
                            color="danger" variant="outlined"
                            loading={busy === 'reject'}
                            disabled={!!busy}
                            onClick={handleReject}
                            icon={<AiOutlineClose size={13}/>}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

// ─── renderItem factory ───────────────────────────────────────────────────────

export const renderModerationItem = ({onApprove, onReject}) => (item) => (
    <ModerationItem
        key={item?.id}
        item={item}
        onApprove={onApprove}
        onReject={onReject}
    />
)

// ─── Main component ───────────────────────────────────────────────────────────

const WorkspaceModerationList = ({owner_id, owner_type}) => {
    const t = useTranslate()
    const [refreshToken, setRefreshToken] = useState(null)

    const type    = useRouteSearchParam(filter_actions.type)
    const keyword = useRouteSearchParam(filter_actions.keyword)

    const onRefresh = () => setRefreshToken(Date.now())

    const {onApprove, onReject} = useApproval({
        notifyListeners: {
            onApproved: onRefresh,
            onRejected: onRefresh,
        },
    })

    const refresh_deps = useMemo(
        () => [owner_id, owner_type, refreshToken, type, keyword],
        [owner_id, owner_type, refreshToken, type, keyword]
    )

    const baseQuery = useMemo(
        () => bindQueryParams([{ownerType: owner_type}, {ownerId: owner_id}]),
        [owner_id, owner_type]
    )

    const [pager_widget] = usePaginationHelper({
        sortHeader:      null,
        SortByWidget:    NullAsDefault,
        isToggleDefault: true,
        AdvancedFilter:  ModerationFilter,
        // renderFilter:    (child, filterWidget) => filterWidget,
        query_params:    filter_actions,
        withKey:         createPagerKey(refresh_deps),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query:        baseQuery,
            PaginationMeta: NullAsDefault,
            apiInfo:      moderation_getOrganizationPendingItems_Api,
            renderItem:   renderModerationItem({onApprove, onReject}),
            renderWrap:   (child, {total: count}) => (
                <div className="flex flex-col">
                    {count > 0 && (
                        <div className="px-4 py-2 background-100 border-b border-color-50">
                            <span className="text-xs text-blue-600 italic">
                                {t('there are')} <strong>{count}</strong> {t('pending items')}
                            </span>
                        </div>
                    )}
                    <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                        {child}
                    </div>
                </div>
            ),
        },
        onInit: ({onFilterChange = NullAsDefault}) => {
            onFilterChange({type, keyword})
        },
        filter_params: {type, keyword},
        dependencies:  [owner_id, owner_type, refreshToken, type, keyword],
        renderHeader:  ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-3">
                {filterBar}
                {searchBar}
            </div>
        ),
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('moderation queue')}
                    </h2>
                    <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
            </div>
            {pager_widget}
        </div>
    )
}

export default compose(
    fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
        owner_id:   getId(organization),
        owner_type: getType(organization),
    }))
)(WorkspaceModerationList)
